import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '@/lib/api/types';
import type { Airline } from '@/types/airline';
import { fetchAirlineDictionary } from './api';

export type AirlinesErrorKey = 'airlines.errorForbidden' | 'airlines.errorLoad';

/** What one completed load produced, tagged with the load it answered. */
interface Outcome {
  nonce: number;
  airlines: Airline[];
  errorKey: AirlinesErrorKey | null;
}

export interface AirlineDictionaryState {
  airlines: Airline[];
  isLoading: boolean;
  errorKey: AirlinesErrorKey | null;
  reload: () => void;
  /**
   * Folds an airline the server just returned back into the list.
   *
   * Every write answers with the saved row, so re-reading a thousand carriers to
   * learn what was just handed back would be a large response for an answer already
   * in hand — and it would blank the table the reader is looking at while it ran.
   */
  apply: (airline: Airline) => void;
  /** Drops a deleted airline, for the same reason. */
  remove: (id: number) => void;
}

/**
 * The airline dictionary.
 *
 * `GET /airline/list` takes no filter, no sort and no page — it returns every carrier
 * in one response — so this hook's only job is that one request, and the narrowing,
 * ordering and paging all happen over the array it produces.
 *
 * **Loading is derived, not stored.** The state is the outcome of the last completed
 * load tagged with the load it answered; "loading" is that tag not matching the load
 * being asked for. A `setLoading(true)` at the top of the effect sets state during the
 * commit and cascades an extra render.
 */
export const useAirlineDictionary = (): AirlineDictionaryState => {
  const [nonce, setNonce] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchAirlineDictionary(controller.signal)
      .then((airlines) => {
        setOutcome({ nonce, airlines, errorKey: null });
      })
      .catch((caught: unknown) => {
        // An abort is not an error: a newer load is already in flight and its outcome
        // is the one that should win.
        if (controller.signal.aborted) return;

        setOutcome({
          nonce,
          airlines: [],
          // A 403 is not a failure to retry — telling a role that may not read this
          // to check their connection sends them looking in the wrong place.
          errorKey:
            caught instanceof ApiError && caught.isForbidden
              ? 'airlines.errorForbidden'
              : 'airlines.errorLoad',
        });
      });

    return () => controller.abort();
  }, [nonce]);

  const reload = useCallback(() => setNonce((current) => current + 1), []);

  const hasList = outcome !== null;

  /**
   * Rewrites the list in hand.
   *
   * Nothing to fold into yet means the first load is still in flight and may have
   * left before this write, so it cannot be trusted to contain the result. Starting a
   * new one discards it — a stale nonce loses — and the list that lands is guaranteed
   * to be current.
   */
  const revise = useCallback(
    (change: (airlines: readonly Airline[]) => Airline[]) => {
      if (!hasList) {
        setNonce((current) => current + 1);
        return;
      }

      setOutcome((current) => (current ? { ...current, airlines: change(current.airlines) } : current));
    },
    [hasList],
  );

  const apply = useCallback(
    (saved: Airline) =>
      revise((airlines) =>
        airlines.some((airline) => airline.id === saved.id)
          ? airlines.map((airline) => (airline.id === saved.id ? saved : airline))
          : // A created carrier goes in at the front; the table sorts and pages the
            // array itself, so where it is inserted is not where it appears.
            [saved, ...airlines],
      ),
    [revise],
  );

  const remove = useCallback(
    (id: number) => revise((airlines) => airlines.filter((airline) => airline.id !== id)),
    [revise],
  );

  const isCurrent = outcome?.nonce === nonce;

  return {
    // The list stays on screen while a reload runs, so a refresh does not blank the
    // table under the reader. An error clears it: the last good answer under an error
    // message is worse than none.
    airlines: outcome?.errorKey ? [] : (outcome?.airlines ?? []),
    isLoading: !isCurrent,
    errorKey: isCurrent ? outcome.errorKey : null,
    reload,
    apply,
    remove,
  };
};
